// src/store/slices/employeeAddressSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_BASE_URL;

// Normalize a single address object from backend
const normalizeAddress = (addr) => ({
  id: addr.id,
  employeeId: addr.employee?.id || null,
  addressType: addr.addressType || "", // Will now store as camelCase (e.g., 'current')
  addressNum: addr.addressNum || "",
  addressLine1: addr.addressLine1 || "",
  addressLine2: addr.addressLine2 || "",
  city: addr.city || "",
  municipality: addr.municipality || "",
  province: addr.province || "",
  region: addr.region || "",
  postalCode: addr.postalCode || "",
  country: addr.country || "Philippines",
});

// Fetch all addresses by employee ID
export const fetchAddressesByEmployeeId = createAsyncThunk(
  "employeeAddress/fetchByEmployeeId",
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/employee/address/employee/${employeeId}`);
      if (!Array.isArray(response.data)) return [];
      return response.data.map(normalizeAddress);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Fetch current address by employee ID
export const fetchCurrentAddressByEmployeeId = createAsyncThunk(
  "employeeAddress/fetchCurrentByEmployeeId",
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/employee/address/employee/${employeeId}/current`);
      return normalizeAddress(response.data);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Fetch permanent address by employee ID
export const fetchPermanentAddressByEmployeeId = createAsyncThunk(
  "employeeAddress/fetchPermanentByEmployeeId",
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/employee/address/employee/${employeeId}/permanent`);
      return normalizeAddress(response.data);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Copy current address → permanent
export const copyCurrentToPermanentAddress = createAsyncThunk(
  "employeeAddress/copyCurrentToPermanent",
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/employee/address/employee/${employeeId}/copy-current-to-permanent`);
      console.log(employeeId)
      return normalizeAddress(response.data);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Create a new address
export const createEmployeeAddress = createAsyncThunk(
  "employeeAddress/create",
  async (data, { rejectWithValue }) => {
    try {
      const addr = (await axios.post(`${API_URL}/employee/address`, data)).data;
      return normalizeAddress(addr);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Update an existing address
export const updateEmployeeAddressById = createAsyncThunk(
  "employeeAddress/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const addr = (await axios.put(`${API_URL}/employee/address/${id}`, data)).data;
      return normalizeAddress(addr);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Delete an address
export const deleteEmployeeAddressById = createAsyncThunk(
  "employeeAddress/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/employee/address/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Check Employee Completeness
export const checkEmployeeCompleteness = createAsyncThunk(
  "employeeAddress/checkCompleteness",
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${API_URL}/employee/${employeeId}/check-completeness`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Completeness check failed");
    }
  }
);

const employeeAddressSlice = createSlice({
  name: "employeeAddress",
  initialState: {
    addresses: [],
    selectedAddress: null,
    current: null,
    permanent: null,
    status: "idle",
    completenessStatus: "idle",
    error: null,
  },
  reducers: {
    clearSelectedAddress: (state) => {
      state.selectedAddress = null;
      state.status = "idle";
      state.completenessStatus = "idle";
      state.error = null;
      state.current = null;
      state.permanent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all addresses
      .addCase(fetchAddressesByEmployeeId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.addresses = action.payload;
      })

      // Fetch current address
      .addCase(fetchCurrentAddressByEmployeeId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.current = action.payload;
      })

      // Fetch permanent address
      .addCase(fetchPermanentAddressByEmployeeId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.permanent = action.payload;
      })

      // Copy current → permanent
      .addCase(copyCurrentToPermanentAddress.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.permanent = action.payload;
        state.addresses.push(action.payload);
      })

      // Create address
      .addCase(createEmployeeAddress.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.addresses.push(action.payload);
        // Updated to use camelCase strings
        if (action.payload.addressType === "current") state.current = action.payload;
        if (action.payload.addressType === "permanent") state.permanent = action.payload;
      })

      // Update address
      .addCase(updateEmployeeAddressById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.addresses = state.addresses.map((addr) =>
          addr.id === action.payload.id ? action.payload : addr
        );
        // Updated to use camelCase strings
        if (action.payload.addressType === "current") state.current = action.payload;
        if (action.payload.addressType === "permanent") state.permanent = action.payload;
      })

      // Delete address
      .addCase(deleteEmployeeAddressById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.addresses = state.addresses.filter((addr) => addr.id !== action.payload);
        if (state.current?.id === action.payload) state.current = null;
        if (state.permanent?.id === action.payload) state.permanent = null;
      })

      // Check Completeness
      .addCase(checkEmployeeCompleteness.pending, (state) => {
        state.completenessStatus = "loading";
      })
      .addCase(checkEmployeeCompleteness.fulfilled, (state) => {
        state.completenessStatus = "succeeded";
      })
      .addCase(checkEmployeeCompleteness.rejected, (state, action) => {
        state.completenessStatus = "failed";
        state.error = action.payload;
      })

      // Global Loading/Failed Matchers (excluding completeness)
      .addMatcher(
        (action) => action.type.endsWith("/pending") && !action.type.includes("checkCompleteness"),
        (state) => { state.status = "loading"; state.error = null; }
      )
      .addMatcher(
        (action) => action.type.endsWith("/rejected") && !action.type.includes("checkCompleteness"),
        (state, action) => {
          state.status = "failed";
          state.error = action.payload;
        }
      );
  },
});

export const { clearSelectedAddress } = employeeAddressSlice.actions;
export default employeeAddressSlice.reducer;